import { pdf } from '@react-pdf/renderer';
import { Buffer } from 'buffer';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useMediaQuery from 'hooks/useMediaQuery';

import {
	getPrintSettings,
	setIsUserSelectedLocale,
	setPrintLocale,
	setPrintSettings
} from 'pages/Settings/Print/PrintSettingsSlice';
import usePrintUtils from 'pages/Settings/Print/hooks/usePrintUtils';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';
import PATHS from 'utils/paths';

import MainTemplate from './MainTemplate';
import usePrintLocale from './usePrintLocale';

const OTOAWB = () => {
	// States
	const [searchParams] = useSearchParams();
	const enc = useMemo(() => searchParams.get('enc'), [searchParams]);

	const [awbData, setAWBData] = useState(null);
	const [orderData, setOrderData] = useState(null);
	const [awbPrintSettings, setAWBPrintSettings] = useState(null);

	// Hooks
	const navigate = useNavigate();
	const { showError } = useAlert();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { mobile = false } = useSelector((state) => state?.layout);
	const { isUserSelectedLocale, printLocale } = useSelector((state) => state?.printSettings);

	const isMobileDevice = useMediaQuery(Breakpoints.mobile);

	const isAuthorized = useAccessControl();
	const { fetchOrderPrintData, updatePrintStatus, fetchAWBData, handlePDFDownload, pdfFile, mergePDF, isLoading } =
		usePrintUtils();
	const { fetchLanguage } = usePrintLocale();

	// static
	const langs = useMemo(() => languages.map((language) => language.code), []);
	const defaultColSpan = {
		md: 2
	};

	const getPropJSON = useCallback(
		async (enc) => {
			const printSettings = await dispatch(getPrintSettings(enc)).unwrap();

			const awbPrintPropJSON = printSettings?.awbPrintPropJSON;

			if (!awbPrintPropJSON) return AppLogger.error('awbPrintPropJSON not found');

			setAWBPrintSettings(awbPrintPropJSON);
			!isUserSelectedLocale && dispatch(setPrintLocale(awbPrintPropJSON?.language));
		},
		[isUserSelectedLocale]
	);

	const fetchData = useCallback(async (enc) => {
		// fetch awb data
		const awbData = await fetchAWBData([], undefined, enc);
		// fetch order data
		if (awbData) {
			setAWBData(awbData.items);

			const otoAWBIDs = awbData?.items
				?.filter((item) => item?.errorCode === 'printAWBNA' && item?.shipmentId)
				.map((item) => item.id);

			const allIDs = awbData?.items?.filter((item) => item?.shipmentId).map((item) => item.id);

			const printOrderData = await fetchOrderPrintData(allIDs, undefined, enc);

			if ((printOrderData && Array.isArray(printOrderData) && printOrderData.length === 0) || !printOrderData) {
				showError({ header: 'printAWBNA' });
				return;
			}

			if (!otoAWBIDs?.length) {
				setOrderData([]);
				return;
			}

			const AWBNAOrderData = printOrderData?.filter((order) => otoAWBIDs.includes(order.ID));

			setOrderData(AWBNAOrderData);
		} else {
			showError({
				message: 'printAWBNA'
			});
		}
	}, []);

	const createPDF = useCallback(
		async (awbData, orderPrintData) => {
			if (!awbData) return;
			if (!orderPrintData) return;
			const encObj = JSON.parse(atob(enc));
			const useGrayScaleLogo = encObj?.zplLogo ?? false;

			const successfulIDs = new Set();

			await mergePDF(async (merger) => {
				// separate remote and oto awb
				for (const item of awbData) {
					if (item?.filePath) {
						// merge remote AWBs
						try {
							const response = await fetch(item?.filePath);
							const pdfBlob = await response.blob();
							await merger.add(pdfBlob);
							successfulIDs.add(item.id);
						} catch {
							showError({ header: 'printAWBNA' });
						}
					} else if (item?.errorCode === 'printAWBNA') {
						// merge oto AWBs
						try {
							const { id } = item;
							const order = orderPrintData.find((order) => order.ID === id);
							if (order) {
								// split itemsList array into 2 element arrays and write each array into a page
								const itemList = order?.items ? [...order.items] : [];
								const splitItemList = [];
								// print each page only with 4 items
								if (awbPrintSettings?.showOrderItems) {
									while (itemList.length > 0) {
										splitItemList.push(itemList.splice(0, 4));
									}
								}

								// multiple box order
								if (order?.boxes?.length > 0 || order?.packageCount > 1) {
									const { boxes, packageCount } = order;

									const boxCount = Math.max(boxes?.length ?? 0, packageCount ?? 0);
									for (let i = 0; i < boxCount; i += 1) {
										if (!splitItemList.length) {
											const pdfBlob = await pdf(
												React.createElement(
													MainTemplate,
													{
														propJSON: awbPrintSettings,
														boxIndex: i,
														boxCount,
														useGrayScaleLogo,
														companyLogo: order?.CompanyLogo ?? null,
														isAuthorizedOMS: isAuthorized('OMSModule'),
														fieldName: 'awbPrintPropJSON',
														successMessage: item?.successMessage ?? null,
														countryISO: order?.receiverCountry,
														...order,
														items: []
													},
													{ key: `${order?.orderID}-${i}-${id}` }
												)
											).toBlob();
											await merger.add(pdfBlob);
											successfulIDs.add(item.id);
										} else {
											for await (const [splitIndex, items] of splitItemList.entries()) {
												const pdfBlob = await pdf(
													React.createElement(
														MainTemplate,
														{
															propJSON: awbPrintSettings,
															boxIndex: i,
															boxCount,
															useGrayScaleLogo,
															companyLogo: order?.CompanyLogo ?? null,
															isAuthorizedOMS: isAuthorized('OMSModule'),
															fieldName: 'awbPrintPropJSON',
															successMessage: item?.successMessage ?? null,
															countryISO: order?.receiverCountry,
															...order,
															items
														},
														{ key: `${order?.orderID}-${splitIndex}-${id}` }
													)
												).toBlob();
												await merger.add(pdfBlob);
												successfulIDs.add(item.id);
											}
										}
									}
								} else {
									if (!splitItemList.length) {
										const pdfBlob = await pdf(
											React.createElement(
												MainTemplate,
												{
													propJSON: awbPrintSettings,
													boxIndex: 0,
													boxCount: 1,
													useGrayScaleLogo,
													companyLogo: order?.CompanyLogo ?? null,
													isAuthorizedOMS: isAuthorized('OMSModule'),
													fieldName: 'awbPrintPropJSON',
													successMessage: item?.successMessage ?? null,
													countryISO: order?.receiverCountry,
													...order,
													items: []
												},
												{ key: `${order?.orderID}-${id}` }
											)
										).toBlob();
										await merger.add(pdfBlob);
										successfulIDs.add(item.id);
									} else {
										for await (const [splitIndex, items] of splitItemList.entries()) {
											const pdfBlob = await pdf(
												React.createElement(
													MainTemplate,
													{
														propJSON: awbPrintSettings,
														boxIndex: 0,
														boxCount: 1,
														useGrayScaleLogo,
														companyLogo: order?.CompanyLogo ?? null,
														fieldName: 'awbPrintPropJSON',
														successMessage: item?.successMessage ?? null,
														countryISO: order?.receiverCountry,
														...order,
														items
													},
													{ key: `${order?.orderID}-${splitIndex}-${id}` }
												)
											).toBlob();
											await merger.add(pdfBlob);
											successfulIDs.add(item.id);
										}
									}
								}
							}
						} catch {
							showError({ header: 'printAWBNA' });
							AppLogger.error('PrintAWB : No file path or error code!');
						}
					}
				}

				if (successfulIDs.size > 0) {
					const buffer = await merger.saveAsBuffer();
					const u8 = new Uint8Array(buffer);
					const b64 = Buffer.from(u8).toString('base64');
					window.awb = b64;
				}

				if (successfulIDs.size > 0) {
					updatePrintStatus(Array.from(successfulIDs), enc);
				}

				// merge all pages into one pdf
				if (window.ReactNativeWebView) {
					const buffer = await merger.saveAsBuffer();
					const u8 = new Uint8Array(buffer);
					const b64 = Buffer.from(u8).toString('base64');
					window.ReactNativeWebView.postMessage(b64);
				} else {
					const mergedPdf = await merger.saveAsBlob();
					return mergedPdf;
				}
			});
		},
		[awbPrintSettings]
	);

	const handleLanguageChange = useCallback(
		async (value) => {
			dispatch(setPrintLocale(value)).then(() => {
				dispatch(setIsUserSelectedLocale(true));
				dispatch(
					setPrintSettings({
						awbPrintPropJSON: { ...awbPrintSettings, language: value }
					})
				);
			});
		},
		[awbPrintSettings]
	);

	const getPrintData = useCallback(async (enc) => {
		if (!enc) return;
		try {
			await getPropJSON(enc);
			fetchData(enc);
		} catch (error) {
			navigate(PATHS.PDF_ERROR);
			AppLogger.error(error);
			showError();
		}
	}, []);

	useEffect(() => {
		getPrintData(enc);
	}, [enc]);

	useEffect(() => {
		setAWBPrintSettings((prevState) => ({
			...prevState,
			...(printLocale && { language: printLocale })
		}));
	}, [printLocale]);

	useEffect(() => {
		if (!awbPrintSettings) return;
		if (!awbData) return;
		if (!orderData) return;
		fetchLanguage(awbPrintSettings?.language).then(() => createPDF(awbData, orderData));
	}, [awbData, orderData, awbPrintSettings]);

	return (
		<>
			{!isLoading ? (
				<>
					<ModalTitle>
						<Typography className="me-3" variant="p1">
							airWaybill
						</Typography>

						<FormItem colSpan={defaultColSpan}>
							<EnumSelect
								valueRemovable={false}
								data={langs}
								value={printLocale}
								setValue={(value) => {
									handleLanguageChange(value);
								}}
								searchable={false}
							/>
						</FormItem>
						{mobile && pdfFile && (
							<FormItem colSpan={defaultColSpan}>
								<Button fullWidth={true} onClick={() => handlePDFDownload(pdfFile, `AWB`)}>
									{t('download')}
								</Button>
							</FormItem>
						)}
					</ModalTitle>
					{!isMobileDevice && (
						<ModalContent
							style={{
								height: '90dvh'
							}}>
							{pdfFile ? (
								<div
									style={{ height: '100%' }}
									dangerouslySetInnerHTML={{
										__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
									}}
								/>
							) : (
								<Loading />
							)}
						</ModalContent>
					)}
				</>
			) : (
				<Loading />
			)}
		</>
	);
};

export default OTOAWB;
